import React, { useEffect, useState } from "react";
import {
  requestBillingInputCode,
  requestListEbillingUsage,
  resetBillingInputCode,
  useAuthState,
  useEbillingDispatch,
  useEbillingState,
} from "../../Context";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../Components/Button";
import { useTranslation } from "react-i18next";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { NumericFormat } from "react-number-format";
import {
  ChevronDownIcon,
  ExclamationCircleIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import FormLabel from "../../Components/FormLabel";
import { classNames, getErrorMessages } from "../../Utils";
import Partner from "../../Components/Partner";
import Badge from "../../Components/Badge";
import { ModalConfirm } from "../../Components/ModalConfirm";
import InputSkeleton from "../../Components/InputSkeleton";
import { Modal } from "../../Components/Modal";
import { EBILLING_TERMS } from "../../Constants";

const dailyQuota = 3;

const EbillingInputCodeForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idParam = searchParams.get("id") || "";

  const [showErrorModal, setshowErrorModal] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [showPremiumModal, setshowPremiumModal] = useState(false);
  const [termsAgree, settermsAgree] = useState(true);
  const [openModalTerms, setopenModalTerms] = useState(false);
  const [submitted, setsubmitted] = useState(false);
  const [billingCodeForms, setbillingCodeForms] = useState([
    { id: 1, rowOpen: true, code: idParam.length <= 15 ? idParam : "" },
  ]);
  const [quotalLeft, setquotalLeft] = useState(0);
  const [billingCodeFormErrors, setbillingCodeFormErrors] = useState([]);

  const dispatch = useEbillingDispatch();

  const { user } = useAuthState();
  const {
    usage,
    loading,
    error,
    billingCodes,
    billingCodesLoading,
    billingCodesError,
  } = useEbillingState();

  const billingCodeErrors = billingCodesError?.response?.body
    ? JSON.parse(billingCodesError.response.body)
    : null;

  const updateBillingCodeErrors = billingCodeErrors?.error?.codes;

  useEffect(() => {
    return () => {
      resetBillingInputCode(dispatch);
      setbillingCodeFormErrors([]);
    };
  }, [dispatch]);

  useEffect(() => {
    if (user.id) {
      if (user.membership === "FREE") {
        requestListEbillingUsage(dispatch, { userID: user.id });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  useEffect(() => {
    setquotalLeft(dailyQuota - usage);
  }, [usage]);

  useEffect(() => {
    if (billingCodes?.length > 0) {
      setshowSuccessModal(true);
    }
  }, [billingCodes?.length]);

  useEffect(() => {
    if (error || billingCodesError) {
      setshowErrorModal(true);
    }
  }, [error, billingCodesError]);

  useEffect(() => {
    if (submitted) {
      const errors = billingCodeForms.map((item) =>
        validateBillingCode(item.code)
      );
      setbillingCodeFormErrors(errors);
    }
  }, [submitted, billingCodeForms]);

  const validateBillingCode = (code) => {
    if (!code.trim()) return "billingCodeEmpty";
    if (code.trim().length < 15) return "billingCodeInvalid";
    return null;
  };

  const onSubmit = () => {
    setsubmitted(true);
    const errors = billingCodeForms.map((item) =>
      validateBillingCode(item.code)
    );
    setbillingCodeFormErrors(errors);

    if (errors.every((error) => !error)) {
      const codes = billingCodeForms.map((item) => item.code);
      requestBillingInputCode(dispatch, { codes });
    }
  };

  const onNavigation = () => {
    if (billingCodes?.length > 0) {
      if (billingCodes.length === 1) {
        navigate(`/ebilling/${billingCodes[0]}`);
      } else {
        navigate("/ebilling");
      }
    }
    setshowSuccessModal(false);
  };

  const handleInputChange = (index, value) => {
    const updatedCodes = [...billingCodeForms];
    updatedCodes[index].code = value;
    setbillingCodeForms(updatedCodes);
  };

  const toggleRow = (id) => {
    const modifiedRows = billingCodeForms.map((billingCode) => {
      if (billingCode.id === id)
        return { ...billingCode, rowOpen: !billingCode.rowOpen };
      return billingCode;
    });
    setbillingCodeForms(modifiedRows);
  };

  const addForm = () => {
    if (billingCodeForms.length < 10 && user.membership === "PREMIUM") {
      const updatedCodes = billingCodeForms.map((billingCode) => ({
        ...billingCode,
        rowOpen: false,
      }));
      setbillingCodeForms([
        ...updatedCodes,
        { id: billingCodeForms.length + 1, rowOpen: true, code: "" },
      ]);
    } else {
      setshowPremiumModal(true);
    }
  };

  const removeForm = (index) => {
    const updatedCodes = billingCodeForms.filter((_, i) => i !== index);
    if (updatedCodes.length === 1) {
      updatedCodes[0] = { ...updatedCodes[0], rowOpen: true };
    }
    setbillingCodeForms(updatedCodes);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-4 lg:gap-x-8">
      {(error || billingCodesError) && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(error || billingCodesError)}
          onConfirm={() => {
            setshowErrorModal(false);
          }}
        />
      )}
      {billingCodes?.length > 0 && (
        <ModalConfirm
          open={showSuccessModal}
          type="SUCCESS"
          setOpen={(open) => {
            setshowSuccessModal(open);
          }}
          title="Success"
          description="billingCodeSuccessDescription"
          onConfirm={onNavigation}
        />
      )}
      {showPremiumModal && (
        <ModalConfirm
          open={showPremiumModal}
          type="WARNING"
          setOpen={(open) => {
            setshowPremiumModal(open);
          }}
          title="Premium Feature"
          description="premiumFeatureBulkBillingDescription"
          onConfirm={() => {
            setshowPremiumModal(false);
            navigate("/subscription");
          }}
        />
      )}
      <Modal
        title="E-Billing Terms"
        show={openModalTerms}
        onClose={() => setopenModalTerms(false)}
      >
        <div className="flex flex-col text-sm text-gray-700 p-4 gap-4">
          {EBILLING_TERMS.map((item, index) => (
            <div key={index} className="flex gap-4">
              <span>{index + 1}.</span>
              <span>{t(item.label)}</span>
            </div>
          ))}
        </div>
      </Modal>
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-sm divide-y divide-gray-200">
          {user.membership === "FREE" && (
            <div className="py-4 px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row space-y-2 justify-between sm:items-center">
                {loading ? (
                  <div className="animate-pulse">
                    <div className="h-4 w-24 my-1 bg-gray-300 rounded-md"></div>
                    <div className="h-4 w-48 my-1 bg-gray-300 rounded-md"></div>
                  </div>
                ) : (
                  <div>
                    <Badge title={`${user.membership} MEMBER`} type="alert" />
                    <p className="font-medium text-gray-900 mt-1">
                      {t("E-Billing Quota Today")}: {quotalLeft}
                    </p>
                  </div>
                )}
                <Button
                  onClick={() => navigate("/subscription")}
                  outline
                  title="Become Premium Member"
                />
              </div>
            </div>
          )}
          {billingCodeForms.map((billingCode, index) => (
            <Disclosure
              defaultOpen={true}
              as="div"
              key={index}
              className="py-4 px-4 sm:px-6"
            >
              {billingCodeForms.length > 1 ? (
                <dt className="text-lg">
                  <DisclosureButton
                    as="div"
                    className="text-left w-full flex justify-between items-start text-gray-400"
                  >
                    <div className="block sm:inline-flex justify-start items-center gap-4 divide-x-0 sm:divide-x divide-gray-200">
                      <span className="w-full sm:w-auto font-medium text-gray-900">
                        {t("billingCode")} #{index + 1}
                      </span>
                      {billingCode.rowOpen ? null : (
                        <div className="w-full sm:w-auto text-sm px-0 sm:px-4 text-gray-900">
                          {billingCode.code}
                        </div>
                      )}
                    </div>
                    <div className="ml-6 gap-4 inline-flex justify-end">
                      <span className="h-7 flex items-center">
                        {!billingCode.rowOpen &&
                          (updateBillingCodeErrors?.[index] ||
                            billingCodeFormErrors?.[index]) && (
                            <ExclamationCircleIcon
                              className="h-6 w-6 text-red-500"
                              aria-hidden="true"
                            />
                          )}
                      </span>
                      <span className="h-7 flex items-center">
                        <button onClick={() => removeForm(index)}>
                          <TrashIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </span>
                      <span className="h-7 flex items-center">
                        <button onClick={() => toggleRow(billingCode.id)}>
                          <ChevronDownIcon
                            className={classNames(
                              billingCode.rowOpen ? "-rotate-180" : "rotate-0",
                              "h-6 w-6 transform"
                            )}
                            aria-hidden="true"
                          />
                        </button>
                      </span>
                    </div>
                  </DisclosureButton>
                </dt>
              ) : null}
              {billingCode.rowOpen ? (
                <DisclosurePanel as="dd" className="mt-2 pr-10" static>
                  {billingCodesLoading ? (
                    <InputSkeleton rows={1} />
                  ) : (
                    <>
                      <FormLabel
                        htmlFor="billingCode"
                        required
                        label={t("billingCode")}
                      />
                      <NumericFormat
                        type="text"
                        name="billingCode"
                        id="billingCode"
                        allowLeadingZeros
                        className={classNames(
                          updateBillingCodeErrors?.[index] ||
                            billingCodeFormErrors?.[index]
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                          "mt-1 block w-full text-sm rounded-md shadow-sm"
                        )}
                        placeholder={t("billingCodePlaceholder")}
                        displayType="input"
                        value={billingCode.code}
                        onChange={(e) =>
                          handleInputChange(index, e.target.value)
                        }
                        isAllowed={({ value }) => value.length <= 15}
                      />
                      {(updateBillingCodeErrors?.[index] ||
                        billingCodeFormErrors?.[index]) && (
                        <div className="mt-1 text-red-600 text-sm">
                          {t(
                            updateBillingCodeErrors?.[index] ||
                              billingCodeFormErrors?.[index]
                          )}
                        </div>
                      )}
                    </>
                  )}
                </DisclosurePanel>
              ) : null}
            </Disclosure>
          ))}
          <div className="py-4 px-4 sm:px-6">
            <div className="flex items-center">
              <input
                name="sourceIncome"
                type="checkbox"
                checked={termsAgree}
                onChange={() => {
                  settermsAgree(!termsAgree);
                }}
                className="focus:ring-blue-500 h-4 w-4 text-blue-500 border-gray-300"
              />
              <div className="flex ml-3 text-sm font-medium gap-1">
                <label className=" text-gray-700">{t("Agree")}</label>
                <button
                  onClick={() => setopenModalTerms(true)}
                  className=" text-blue-600"
                >
                  {t("E-Billing Terms")}
                </button>
              </div>
            </div>
          </div>
          <div className="flex-1 flex flex-wrap items-center justify-between py-4 px-4 sm:px-6 gap-4">
            <Button
              disabled={billingCodeForms.length === 10}
              type="button"
              title="Add Another"
              onClick={addForm}
              outline
            />
            <Button
              disabled={!termsAgree || billingCodesLoading}
              loading={billingCodesLoading}
              data-testid="ebilling-create-button"
              type="button"
              className="w-full sm:w-auto"
              title={`${"Input"}${
                billingCodeForms.length && ` ${billingCodeForms.length}`
              } E-Billing`}
              onClick={onSubmit}
            />
          </div>
        </div>
        <div className="mt-4 bg-white border border-gray-200 py-4 px-4 sm:px-6 rounded-md sm:rounded-lg shadow">
          <Partner
            title="Collaborating with BRI"
            description="Official PJAP Directorate General of Taxes"
            url="https://pajak.go.id/id/pt-bank-rakyat-indonesia-persero-tbk"
          />
        </div>
      </div>
    </div>
  );
};

export default EbillingInputCodeForm;
